import { useUser } from '@clerk/react';
import { useEffect } from 'react';
import {
  autoOpenWhatsNew,
  closeWhatsNew,
  markSeen,
  openWhatsNew,
  useSeenIds,
  useWhatsNewUi,
} from './whats-new.store';
import { RELEASES, type Announcement } from './whats-new.util';

/**
 * The What's new announcements and what to do with them, for the card and the
 * sidebar button. Content comes from RELEASES in code. An announcement is
 * unseen until the card is closed with it on screen. The card opens by
 * itself, once per load, while any is unseen, and the sidebar's box opens it
 * whenever it is clicked. Opening reads nothing; closing does.
 */
export function useWhatsNew(): {
  /** On the card: every announcement, while it's open. */
  announcements: Announcement[];
  /** Anything to announce at all; the sidebar button shows only then. */
  hasAny: boolean;
  /** Something on offer this user hasn't closed the card over yet. */
  hasUnread: boolean;
  /** The card is up. */
  visible: boolean;
  replayKey: number;
  /** Where the card opens: the first announcement unseen when it opened. */
  startIndex: number;
  /** Open the card, at the first announcement not seen yet. */
  open: () => void;
  /** Close the card; everything on it counts as seen. */
  close: () => void;
} {
  const ui = useWhatsNewUi();
  const { user } = useUser();
  const userId = user?.id ?? null;
  const seen = useSeenIds(userId);

  const source = RELEASES;
  const firstUnread = userId
    ? source.findIndex((a) => !seen.includes(a.id))
    : -1;
  const unseenKey = source
    .filter((a) => !seen.includes(a.id))
    .map((a) => a.id)
    .join('\n');
  const announcements = ui.open ? source : [];

  // Waits for the user: until it has settled there is nothing unseen to
  // find. Both the card and the button run this; the store lets only the
  // first open the card.
  useEffect(() => {
    if (userId && firstUnread >= 0)
      autoOpenWhatsNew(userId, firstUnread, unseenKey.split('\n'));
  }, [userId, firstUnread, unseenKey]);

  return {
    announcements,
    hasAny: source.length > 0,
    hasUnread: firstUnread >= 0,
    visible: announcements.length > 0,
    replayKey: ui.nonce,
    startIndex: ui.start,
    open: () => openWhatsNew(Math.max(0, firstUnread)),
    close: () => {
      if (userId)
        markSeen(
          userId,
          source.map((a) => a.id)
        );
      closeWhatsNew();
    },
  };
}
